/**
 * ContractorCrewService — installer (person) ↔ contractor company membership.
 * ─────────────────────────────────────────────────────────────────────────
 * A "child installer" in the Installer Organizations admin page is an
 * individual person — a contractor_crews row linking profiles.id (user_id)
 * to a contractor_id, with a crew_role ("owner" | "lead" | "installer" | …)
 * and status. "Parent" is the contractor company (contractors row) that row
 * points at. Moving a child to a new parent means updating that row's
 * contractor_id.
 *
 * contractor_crews RLS (contractor_crews_write_org) restricts all writes to
 * callers with an active org_memberships row whose org_role is 'owner' or
 * 'manager' for that contractor's organization — that policy is the real,
 * final gate on reassignment; this service does not attempt to replicate it
 * client-side, it just performs the write and surfaces whatever RLS returns.
 */
import { supabase } from "@/lib/supabase";

export interface CrewMember {
  id: string;
  contractorId: string;
  userId: string;
  crewRole: string;
  status: string;
  email: string;
  fullName: string;
  role: string;
}

interface ContractorCrewRow {
  id: string;
  contractor_id: string;
  user_id: string;
  crew_role: string;
  status: string;
}

interface ProfileRow {
  id: string;
  email: string | null;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
}

function displayNameFromProfile(profile: ProfileRow | undefined, userId: string): string {
  if (!profile) return userId;
  const fullName = profile.full_name?.trim();
  if (fullName) return fullName;
  const composed = [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim();
  if (composed) return composed;
  return profile.email?.trim() || userId;
}

export const ContractorCrewService = {
  async fetchCrewMembers(): Promise<CrewMember[]> {
    try {
      const { data: crewRows, error: crewErr } = await supabase
        .from("contractor_crews")
        .select("id, contractor_id, user_id, crew_role, status");
      if (crewErr) {
        console.warn("Error fetching contractor_crews:", crewErr.message);
        return [];
      }
      const rows = (crewRows ?? []) as ContractorCrewRow[];
      if (rows.length === 0) return [];

      const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
      const { data: profileRows, error: profileErr } = await supabase
        .from("profiles")
        .select("id, email, full_name, first_name, last_name, role")
        .in("id", userIds);
      if (profileErr) {
        console.warn("Error fetching profiles for crew members:", profileErr.message);
      }
      const profileById = new Map(
        ((profileRows ?? []) as ProfileRow[]).map((p) => [p.id, p]),
      );

      return rows.map((r) => {
        const profile = profileById.get(r.user_id);
        return {
          id: r.id,
          contractorId: r.contractor_id,
          userId: r.user_id,
          crewRole: r.crew_role,
          status: r.status,
          email: profile?.email ?? "",
          fullName: displayNameFromProfile(profile, r.user_id),
          role: profile?.role ?? "",
        };
      });
    } catch (err) {
      console.warn("Exception fetching crew members:", err);
      return [];
    }
  },

  async reassignCrewMember(
    crewRowId: string,
    newContractorId: string,
    actor: { id: string },
    reason?: string,
  ): Promise<{ crewMember: CrewMember | null; error: string | null }> {
    try {
      const { data: crewRow, error: crewErr } = await supabase
        .from("contractor_crews")
        .select("id, contractor_id, user_id, crew_role, status")
        .eq("id", crewRowId)
        .maybeSingle();
      if (crewErr) return { crewMember: null, error: crewErr.message };
      if (!crewRow) return { crewMember: null, error: "Crew member not found." };
      if (crewRow.contractor_id === newContractorId) {
        return { crewMember: null, error: "Installer is already under that parent." };
      }

      const { data: newParent, error: parentErr } = await supabase
        .from("contractors")
        .select("id, status")
        .eq("id", newContractorId)
        .maybeSingle();
      if (parentErr) return { crewMember: null, error: parentErr.message };
      if (!newParent) return { crewMember: null, error: "Target parent installer not found." };
      if (newParent.status !== "active") {
        return { crewMember: null, error: "Target parent installer is not active." };
      }

      const { data: updated, error: updateErr } = await supabase
        .from("contractor_crews")
        .update({ contractor_id: newContractorId })
        .eq("id", crewRowId)
        .select("id, contractor_id, user_id, crew_role, status")
        .single();
      if (updateErr) return { crewMember: null, error: updateErr.message };

      const { error: historyErr } = await supabase.from("contractor_crew_reassignment_history").insert({
        user_id: crewRow.user_id,
        from_contractor_id: crewRow.contractor_id,
        to_contractor_id: newContractorId,
        changed_by: actor.id,
        reason: reason ?? null,
      });
      if (historyErr) {
        console.warn("Failed to record contractor_crew_reassignment_history:", historyErr.message);
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("id, email, full_name, first_name, last_name, role")
        .eq("id", crewRow.user_id)
        .maybeSingle();

      const row = updated as ContractorCrewRow;
      return {
        crewMember: {
          id: row.id,
          contractorId: row.contractor_id,
          userId: row.user_id,
          crewRole: row.crew_role,
          status: row.status,
          email: profile?.email ?? "",
          fullName: displayNameFromProfile(profile as ProfileRow | undefined, row.user_id),
          role: profile?.role ?? "",
        },
        error: null,
      };
    } catch (err) {
      return { crewMember: null, error: String(err) };
    }
  },
};
